import { useEffect, useState, lazy, Suspense, useContext, useRef } from 'react';
import { useMediaQuery } from 'react-responsive';
import { isMobile } from 'react-device-detect';

import styles from './root.module.scss';
import Preloader from '../common/preloader/preloader';
import StartPage from '../start-page/start-page';
import CloseButton from '../common/close-button/close-button';
import { componentLoader } from '../../utils';

import Progress from '../common/progress/progress';
import { Expert } from '../common/expert/expert';
import { SceneToken } from '../../enums/scene-token.enum';
import ApiErrorMessage from '../common/api-error-message/api-error-message';
import { PageWrapper } from './page-wrapper/page-wrapper';
import { PageWrapperMode } from '../../enums/page-wrapper-mode.enum';
import { useConfig } from '../../hooks/config.hook';
import { useQuizData } from '../../hooks/quiz-data.hook';
import { usePluginData } from '../../hooks/plugin-data.hook';
import { useDisplay } from '../../hooks/use-display.hook';
import { useProgress } from '../../hooks/progress.hook';
import { shallowEqual, useDispatch } from 'react-redux';
import { QuizEvent } from '../../enums/quiz-event.enum';
import { Popups } from '../popups/popups';
import { QuizMode } from '../../enums/quiz-mode.enum';
import { BonusesPopup } from '../popups/bonuses-popup/bonuses-popup';
import { SendLeadLoader } from '../common/send-lead-loader/send-lead-loader';
import { device } from '../../services/device-detector.service';
import { getIosVersion } from '../../utils/get-ios-version';
import classNames from 'classnames';
import { ThemeContext } from './theme.context';
import { postMessageService } from '../../services/post-message.service';
import { Footer } from '../common/footer/footer';
import { StylesContext } from '../../contexts/styles-context/styles.context';
import { Theme } from '../../enums/theme.enum';
import { analyticsService } from '../../analytics.service';
import { AnalyticsEvent } from '../../enums/analytics-event.enum';
import { useAppSelector } from '../../hooks/redux.hook';
import { ErrorMessageContext } from './error-message.context';
import { QuizHistoryService, browserHistoryService } from '../../services/history.service';
import { BrowserHistoryCommand } from '../../enums/browser-history-command.enum';
import { rollupResults } from '../../redux/quiz-slice';
import SignUp from '../popups/sign-up/sign-up';
import { ExpertsComment } from '../questions/experts-comment/experts-comment';
import { rollupHistory } from '../../redux/history-slice';

const QuestionContainer = lazy(() => componentLoader<typeof import('../questions/question-container/question-container')>(() => import('../questions/question-container/question-container')));
const FinalFormPage = lazy(() => componentLoader<typeof import('../final-form-page/final-form-page')>(() => import('../final-form-page/final-form-page')));
const PreFinal = lazy(() => componentLoader<typeof import('../pre-final/pre-final')>(() => import('../pre-final/pre-final')));
const FinalPage = lazy(() => componentLoader<typeof import('../final-page/final-page')>(() => import('../final-page/final-page')));
const InfoBlock = lazy(() => componentLoader<typeof import('../info-block/info-block')>(() => import('../info-block/info-block')));

interface RootComponentProps {
  historyService: QuizHistoryService
}

export function RootComponent({ historyService }: RootComponentProps) {
  const themeContext = useContext(ThemeContext);
  const styleContext = useRef(useContext(StylesContext));
  const { messageConfig } = useContext(ErrorMessageContext);
  const { content, display, quiz, settings } = useAppSelector(state => state);
  const service = useAppSelector(state => state.service, shallowEqual);
  const { currentScene, nextScene } = historyService;
  const dispatch = useDispatch();

  useConfig();
  usePluginData();
  useQuizData(service.quizId);

  useEffect(() => {
    postMessageService.send({ event: QuizEvent.LOAD });
    analyticsService.send(AnalyticsEvent.LOAD);
  }, []);

  useEffect(() => {
    historyService?.replace({
      token: SceneToken.START_PAGE,
      status: true
    });
  }, [historyService]);

  useEffect(() => {
    themeContext.changeTheme(settings.theme || Theme.LIGHT);
  }, [settings.theme, themeContext]);

  useEffect(() => {
    if (settings.stylesModifications) {
      styleContext.current.generateStyles(settings.stylesModifications);
    }
  }, [settings.stylesModifications]);

  useDisplay(currentScene, nextScene);

  const { wasOpened, wasPassed } = quiz;

  const minWidth1024 = useMediaQuery({ minWidth: 1024 });
  const minWidth1280 = useMediaQuery({ minWidth: 1280 });
  const progress = useProgress(currentScene, content.currentContent.questions?.length + 1);
  const enabledInfoBlock = minWidth1280 || (minWidth1024 && (currentScene?.token == SceneToken.FORM || currentScene?.token == SceneToken.FINAL));
  const [flyingButtonStatus, includeFlyingButton] = useState(false);

  const fromIos = device.isIOS();
  const iosVersion = getIosVersion();

  useEffect(() => {
    if (!isMobile && settings.flyingButton && service.initialPageIndex == SceneToken.START_PAGE && wasOpened) {
      includeFlyingButton(true);
      analyticsService.send(AnalyticsEvent.SHOW_FLYING_BTN);
    } else includeFlyingButton(false);
  }, [wasOpened, service.initialPageIndex, settings.flyingButton]);

  const closeHandler = () => {
    postMessageService.send({ event: QuizEvent.CLICK_CROSS });
    browserHistoryService.perform(BrowserHistoryCommand.PUSH, null);
    dispatch(rollupResults(historyService.currentScene.subState?.data?.id));
    historyService.rollupHistory();
    analyticsService.send(AnalyticsEvent.CLOSE);
    analyticsService.send(AnalyticsEvent.CLICK_CROSS);
  }

  const labelClickHandler = () => {
    analyticsService.send(AnalyticsEvent.CLICK_TRADEMARK);
  };

  return (<div className={classNames(styles.root, { [styles.fromIos]: fromIos }, styles[`ios-${iosVersion}`])}>
    <Preloader active={display.preloader} />
    {messageConfig.active && <ApiErrorMessage message={messageConfig.message} onSubmit={messageConfig.onSubmit} />}
    {/* {true && <CloseButton currentScene={currentScene.token} onClick={closeHandler}/>} */}
    {(service.mode == QuizMode.POPUP || service?.customSettings?.closeButton) && <CloseButton currentScene={currentScene?.token} onClick={closeHandler} />}

    {display.sendLoader && <SendLeadLoader />}

    {quiz.hasContent && <>

      <Progress
        quizProgress={progress}
        active={display.progressBar}
      />

      {(enabledInfoBlock && currentScene?.token != SceneToken.START_PAGE && currentScene?.token != SceneToken.SIGN_UP) &&
        <Suspense fallback={<div style={{ width: '23%' }} />}>
          <InfoBlock
            active={display.infoBlock}
            content={content.currentContent?.infoBlock}
            bonuses={content.currentContent?.bonuses}
            currentScene={historyService.currentScene}
            bonusAvailability={wasPassed}
          />
        </Suspense>
      }

      {display.expert && content.currentContent.expert &&
        <Expert
          theme="default"
          content={content.currentContent.expert}
          active={display.expert}
          shift={enabledInfoBlock}
        />}

      {display.expert && content.expertComment && settings.expertComments && <ExpertsComment
        active={display.expert && Boolean(content.expertComment)}
        text={content.expertComment}
        maxQuestionCommentLength={Math.max(...content.currentContent.questions.map(q => q.expertComment?.length || 0))}
        height={quiz.expertsCommentHeight}
        shift={enabledInfoBlock}
      />}

      {(currentScene?.token == SceneToken.START_PAGE || currentScene?.token == SceneToken.SIGN_UP) &&
        <StartPage
          bonuses={content.currentContent.bonuses}
          PCcontent={content.currentContent.startPage}
          mobileContent={content.currentContent.mobileStartPage}
          historyService={historyService}
          headerContent={content.currentContent.header}
          hasFlyingButton={flyingButtonStatus}
          hasWhatsAppButton={settings.whatsAppButtonOnStartPage && settings.whatsAppInfo?.enabled}
          wasOpened={wasOpened}
          whatsApp={settings.whatsAppInfo}
          hasSignupForm={Boolean(content.currentContent.signupForm)}
          quizState={quiz}
          labelClickHandler={labelClickHandler}
          footerContent={content.currentContent.footer}
          footerDisclamer={content.currentContent.footerDisclamer}
          settings={settings}
          customLabel={content.currentContent.customLabel}
          domain={service.domain}
          firstQuestion={content.currentContent.questions[0]}
          visualLocation={settings.startPageImagePosition}
        />
      }

      {(currentScene?.token == SceneToken.QUESTION && content.currentContent.questions.length) &&
        <PageWrapper
          mode={enabledInfoBlock ? PageWrapperMode.FLEX : PageWrapperMode.DEFAULT}
          shrink={enabledInfoBlock && display.infoBlock}
        >
          <Suspense fallback={() => <Preloader active={true} />}>
            <QuestionContainer
              questions={content.currentContent.questions}
              historyService={historyService}
              url={service.questionFilters?.url}
              error={quiz.errors[0]?.message}
              expertsCommentHeight={quiz.expertsCommentHeight}
              hasExpertsComment={settings.expertComments}
              backButtonOnTheFirstQuestion={settings.backButtonOnTheFirstQuestion}
            />
          </Suspense>
        </PageWrapper>
      }

      {currentScene?.token == SceneToken.FORM &&
        <PageWrapper
          mode={enabledInfoBlock ? PageWrapperMode.FLEX : PageWrapperMode.DEFAULT}
          shrink={enabledInfoBlock && display.infoBlock}
          shrinkFirst={enabledInfoBlock}
          fullScreenHeight={false}
        >
          <Suspense fallback={() => <Preloader active={true} />}>
            <FinalFormPage
              PCcontent={content.currentContent.finalForm}
              mobileContent={content.currentContent.mobileFinalForm}
              privacyPolicyUrl={content.currentContent.privacyPolicy}
              historyService={historyService}
              whatsApp={settings.whatsAppInfo}
              showWhatsAppForm={display.whatsAppForm}
              displayPopup={display.popupWindow}
              activePopupId={quiz.activePopupWindowID}
              bonuses={content.currentContent.bonuses}
              formViewType={settings.formViewType}
              expertsCommentHeight={quiz.expertsCommentHeight}
              hasExpertsComment={settings.expertComments}
              backButton={settings.backButtonOnTheFinalFormPage}
            />
          </Suspense>
        </PageWrapper>
      }

      {currentScene?.token == SceneToken.RESULT &&
        <PageWrapper
          mode={settings.infoBlock ? PageWrapperMode.FLEX : PageWrapperMode.DEFAULT}
          active={enabledInfoBlock ? display.infoBlock : true}
        >
          <Suspense fallback={() => <Preloader active={true} />}>
            <PreFinal
              header={""}
              dateForm={Boolean(settings.dateForm)}
              historyService={historyService}
            />
          </Suspense>
        </PageWrapper>
      }

      {currentScene?.token == SceneToken.FINAL &&
        <PageWrapper mode={enabledInfoBlock ? PageWrapperMode.FLEX : PageWrapperMode.DEFAULT}
          shrink={enabledInfoBlock && display.infoBlock}
          shrinkFirst={enabledInfoBlock}>
          <Suspense fallback={() => <Preloader active={true} />}>
            <FinalPage
              historyService={historyService}
              onClose={closeHandler}
              content={content.currentContent.finalPage}
              bonuses={content.currentContent.bonuses}
              appointmentDateSent={quiz.appointmentDateSent}
            />
          </Suspense>
        </PageWrapper>
      }

      {(currentScene?.token != SceneToken.SIGN_UP && currentScene?.token != SceneToken.START_PAGE && currentScene?.token != SceneToken.FORM && (!settings.whiteLabel || content.currentContent.customLabel)) && <Footer
        text={content.currentContent.footer}
        mode={"onlyLabel"}
        currentSceneToken={currentScene?.token}
        domain={service.domain}
        customLabel={content.currentContent.customLabel}
        whiteLabel={settings.whiteLabel}
      />}

      {currentScene?.token == SceneToken.SIGN_UP && content.currentContent.signupForm && <SignUp
        historyService={historyService}
        content={content.currentContent.signupForm}
        privacyPolicyUrl={content.currentContent.privacyPolicy}
        onClose={() => browserHistoryService.perform(BrowserHistoryCommand.BACK)}
      />}
    </>}

    <BonusesPopup
      bonuses={content.currentContent.bonuses}
      active={currentScene?.token == SceneToken.QUESTION}
    />

    <Popups
      active={display.popupWindow}
      errors={quiz.errors}
      activePopupWindowID={quiz.activePopupWindowID}
      bonuses={content.currentContent.bonuses}
      customModals={content.currentContent.modal_windows}
    />

  </div>);
}